import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import * as documentService from "../../../../models/services/documentService";
import { AppUtils } from "source/framework/appUtils";
import { List } from "../../../framework/List/List";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Box } from "@mui/material";
import { IDocumentVersionListItem } from "source/models/documentVersion/documentVersionListItem";
import { DocumentVersionType } from "ui/Scripts/source/models/enums";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { DocumentVersionDialog } from "./DocumentVersionDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IDocumentVersionListProp {
    companyId: string;
    folderId: string;
    documentId: string;
}

interface IDocumentVersionListState {
    // List
    intialized: boolean;
    isLoading: boolean;
    items: IDocumentVersionListItem[];
    selectedId: string;

    showEditDialog: boolean;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export class DocumentVersionList extends React.Component<IDocumentVersionListProp, IDocumentVersionListState> {
    constructor(props: IDocumentVersionListProp) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            items: [],
            selectedId: null,
            showEditDialog: false,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
        };
    }

    searchItems = async () => {
        const props = this.props;
        if (!props.documentId) return;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => documentService.getDocumentVersionListItems(props.companyId, props.folderId, props.documentId), false, true, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.state;
        const items = List.getListItems(state.items, state.selectedId, [], result.items, true, false);
        const index = items.items.findIndex(i => i.id === state.selectedId);
        const selectedItem = index > -1 ? items.items[index] : null;
        this.setState({
            intialized: true,
            items: items.items,
            selectedId: selectedItem?.id ?? "",
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < items.items.length - 1
        });
    };

    refreshList = async () => {
        await this.searchItems();
    };

    componentDidMount(): void {
        this.searchItems();
    }

    componentDidUpdate(prevProps: IDocumentVersionListProp, prevState: IDocumentVersionListState) {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevFolderId = prevProps.folderId ? prevProps.folderId : "";
        const folderId = props.folderId ? props.folderId : "";
        const prevDocumentId = prevProps.documentId ? prevProps.documentId : "";
        const documentId = props.documentId ? props.documentId : "";
        if (companyId === prevCompanyId && folderId === prevFolderId && folderId === prevFolderId && documentId === prevDocumentId) return;
        this.searchItems();
    }

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        this.searchItems();
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0 || index >= state.items.length) return;
        this.setState({
            selectedId: state.items[index].id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < state.items.length - 1
        });
    };

    handleOpenDocumentVersion = (id: string) => {
        if (!id) return;
        const state = this.state;
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            selectedId: id,
            showEditDialog: true,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < state.items.length - 1
        });
    };

    handleCloseDocumentVersion = () => {
        this.setState({ showEditDialog: false });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <VerticalContainer
                    main={
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={false}
                            multiSelect={false}
                            globalLoadingInUse={false}
                            height="100%"
                            header={{
                                columns: [
                                    { size: 3, column: "ModifyDate", content: Translations.Time },
                                    { size: 3, column: "UserName", content: Translations.User },
                                    { size: 3, column: "PublishVersion", content: Translations.PublishVersion },
                                    { size: 3, column: "ContentVersion", content: Translations.ContentVersion }
                                ]
                            }}
                            lines={state.items.map((item) => {
                                return {
                                    columns: [
                                        { size: 3, content: <ListLinkValue onClick={() => this.handleOpenDocumentVersion(item.id)}>{Base.timeToDateTimeStr(item.modifyUser.date)}</ListLinkValue> },
                                        {
                                            size: 3,
                                            content: <Box display="flex" sx={{ overflow: "hidden" }}>
                                                <AvatarImage
                                                    id={item.modifyUser.code}
                                                    name={item.modifyUser.name}
                                                    image={item.modifyUser.picture}
                                                    tooltip={item.modifyUser.name}
                                                />
                                                <Box display="flex" sx={{ marginLeft: ".5rem", overflow: "hidden" }}>
                                                    {item.modifyUser.name}
                                                </Box>
                                            </Box>
                                        },
                                        { size: 3, content: item.type === DocumentVersionType.Publish && item.version ? item.version.toString(10) : "" },
                                        { size: 3, content: item.contentVersion ? item.contentVersion.toString(10) : "" }
                                    ],
                                    id: item.id
                                };
                            })}
                            sortColumn={""}
                            sortOrderIsAsc={false}
                            selectedId={state.selectedId}
                            checkedIds={[]}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={null}
                        />
                    }
                />
                {state.showEditDialog &&
                    <DocumentVersionDialog
                        id={state.selectedId}
                        companyId={props.companyId}
                        folderId={props.folderId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onCancel={this.handleCloseDocumentVersion}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}